import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Card, Typography, Select, MenuItem, FormControl } from "@mui/material";
import * as echarts from "echarts";
import { CHART_TOOLTIP_STYLE } from "../const";

const SsidTrafficChart = ({ ssidTraffic = [] }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [viewMode, setViewMode] = useState("total"); // "total" | "uplink" | "downlink"

  // 1. Filter Top 10 SSIDs
  const sortedTop10SsidData = useMemo(() => {
    const listWithTotal = ssidTraffic.map((item) => {
      const sum = (item.daily || []).reduce((acc, curr) => acc + (curr?.total || 0), 0);
      return { ...item, cumulativeTotal: sum };
    });
    // Sort descending
    const sorted = [...listWithTotal].sort((a, b) => b.cumulativeTotal - a.cumulativeTotal);
    // Top 10
    return sorted.slice(0, 10);
  }, [ssidTraffic]);

  // 2. Get unit and scale
  const scaleFactor = 1024 * 1024 * 1024;
  const chartUnit = "GB";

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom || sortedTop10SsidData.length === 0) return;

    const chart = echarts.init(chartDom);
    chartInstance.current = chart;

    // X axis (D1 - D7)
    const daysCount = sortedTop10SsidData[0]?.daily?.length || 7;
    const days = Array.from({ length: daysCount }, (_, idx) => `D${idx + 1}`);

    const series = sortedTop10SsidData.map((item) => {
      const seriesData = (item.daily || []).map((d) => {
        const bytes = d?.[viewMode] || 0;
        return +(bytes / scaleFactor).toFixed(2);
      });

      return {
        name: item.ssid,
        type: "line",
        stack: "traffic",
        areaStyle: {
          opacity: 0.18,
        },
        emphasis: {
          focus: "series",
        },
        smooth: true,
        showSymbol: false,
        data: seriesData,
      };
    });

    const option = {
      color: ["#172664", "#87A2E2", "#22b7db", "#6A5FC2", "#00B61B", "#FDAE2D", "#DB0046", "#C9DCF3", "#B594E3", "#5F6267"],
      grid: {
        top: 20,
        bottom: 45,
        left: 30,
        right: 15,
        containLabel: true,
      },
      legend: {
        bottom: 0,
        left: 30,
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "#fff",
        borderColor: "#e0e4f0",
        borderWidth: 1,
        textStyle: {
          color: "#333",
          fontSize: 12,
        },
        padding: [10, 14],
        extraCssText: CHART_TOOLTIP_STYLE + " min-width: 250px;",
        formatter: (params) => {
          const dayName = params[0].axisValue;
          // Sort items in tooltip descending by value
          const sortedParams = [...params].sort((a, b) => b.value - a.value);

          let tooltipHtml = `
            <div style="font-weight: bold; margin-bottom: 6px; color: #172264;">${dayName}</div>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 16px;">
          `;

          sortedParams.forEach((p) => {
            tooltipHtml += `
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
                <span style="display: flex; align-items: center; gap: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100px;">
                  <span style="display: inline-block; width: 6px; height: 6px; border-radius: 50%; background-color: ${p.color};"></span>
                  ${p.seriesName}
                </span>
                <span style="font-weight: bold;">${p.value} ${chartUnit}</span>
              </div>
            `;
          });

          tooltipHtml += `</div>`;
          return tooltipHtml;
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: days,
        axisLine: {
          lineStyle: {
            color: "#e0e4f0",
          },
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      yAxis: {
        type: "value",
        /* name: `Traffic (${chartUnit})`,
        nameTextStyle: {
          color: "#707ca8",
          fontSize: 10,
          padding: [0, 0, 0, 20],
        }, */
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
        splitLine: {
          lineStyle: {
            color: "#f0f2f8",
          },
        },
      },
      series,
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [sortedTop10SsidData, viewMode, scaleFactor, chartUnit]);

  if (sortedTop10SsidData.length === 0) {
    return (
      <Card
        sx={{
          width: "100%",
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #e0e4f0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          minHeight: "340px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#172264", fontWeight: 600, mb: 2 }}>SSID by Traffic</Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#707ca8", fontSize: "14px" }}>No Data</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        width: "100%",
        p: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            color: "#172264",
            fontWeight: 600,
          }}
        >
          SSID by Traffic
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "12px", color: "#707ca8" }}>View</Typography>
          <FormControl size="small">
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              sx={{
                height: "28px",
                fontSize: "12px",
                color: "#172264",
                fontWeight: 600,
                borderColor: "#e0e4f0",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e4f0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#172264",
                },
              }}
            >
              <MenuItem value="total" sx={{ fontSize: "12px" }}>Total (Uplink + Downlink)</MenuItem>
              <MenuItem value="uplink" sx={{ fontSize: "12px" }}>Uplink</MenuItem>
              <MenuItem value="downlink" sx={{ fontSize: "12px" }}>Downlink</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{ width: "100%", height: "280px" }} ref={chartRef} />
    </Card>
  );
};

export default SsidTrafficChart;
